import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from '@tanstack/react-router';
import { useAppKitAccount, useAppKitProvider, useAppKitNetwork } from '@reown/appkit/react';
import { getCollectionInfo, getVolumeInfo, searchNFTs, getNFTMetadata } from '../../lib/api';
import { ethers } from 'ethers';
import { toast } from 'react-toastify';
import { chainData } from '../../chains';
import { ipfs2md5 } from '../../utils';
import { uploadProxy } from '../../UploadShim';
import { sessionStorageGet } from '../../lib/window-shim';

const IPFS_GATEWAYS = ['https://ipfs.io/ipfs/'];

const resolveIpfsUrl = (uri: string | null | undefined): string | null => {
  if (!uri || typeof uri !== 'string') return null;
  if (uri.startsWith('http')) return uri;
  if (uri.startsWith('ipfs://')) return IPFS_GATEWAYS[0] + uri.slice('ipfs://'.length);
  if (uri.startsWith('ipns://')) return 'https://ipfs.io/ipns/' + uri.slice('ipns://'.length);
  return null;
};

const fetchJsonFromUri = async (uri: string | null | undefined): Promise<any> => {
  const resolved = resolveIpfsUrl(uri);
  if (!resolved) return null;
  try {
    const response = await fetch(resolved, { headers: { Accept: 'application/json' } });
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
};

const basic721Abi = [
  { name: 'tokenURI', type: 'function', inputs: [{ name: 'tokenId', type: 'uint256' }], outputs: [{ type: 'string' }], stateMutability: 'view' },
];

const fetchOnChainMetaForItem = async ({
  provider,
  contractAddress,
  tokId,
}: {
  provider: any;
  contractAddress?: string | null;
  tokId: string | number;
}): Promise<{ metadata: any; uri: string | null } | null> => {
  if (!provider || !contractAddress) return null;
  try {
    const contract = new ethers.Contract(contractAddress, basic721Abi, provider);
    const uri = await contract.tokenURI(tokId);
    if (!uri) return { metadata: null, uri: null };
    const metadata = await fetchJsonFromUri(uri);
    return { metadata, uri };
  } catch {
    return null;
  }
};

const getClientAuthHeaders = (): Record<string, string> => {
  const authKey = sessionStorageGet('authKey');
  const tokenStr = sessionStorageGet('token');
  if (!authKey || !tokenStr) return {};
  try {
    const parsed = JSON.parse(tokenStr);
    const userId = parsed?.account?.id;
    return { Authorization: `Bearer ${authKey}:${userId}`, 'Content-Type': 'application/json' };
  } catch {
    return {};
  }
};

const BATCH_META_UPDATE_PATH = '/api/v1/batchMetaUpdate';

const postBatchMetaUpdate = async (
  baseUrl: string,
  payload: {
    collectionId: string | number;
    contractAddress: string;
    network: string | number;
    items: Array<{
      tokId: string | number;
      hash?: string | null;
      metadata?: any;
      imageUrl?: string | null;
    }>;
  }
): Promise<boolean> => {
  try {
    const response = await fetch(`${baseUrl}${BATCH_META_UPDATE_PATH}`, {
      method: 'POST',
      headers: { ...getClientAuthHeaders() },
      body: JSON.stringify({
        collectionId: payload.collectionId,
        contractAddress: payload.contractAddress,
        network: payload.network,
        items: payload.items.map((item) => ({
          tokId: item.tokId,
          hash: item.hash ?? null,
          metadata: item.metadata ?? null,
          imageUrl: item.imageUrl ?? null,
        })),
      }),
    });
    return response.ok;
  } catch {
    return false;
  }
};

interface InitialData {
    initialCInfo?: any | null;
    initialNfts?: any[];
    initialVolData?: any | null;
}

interface FileItem {
  coll: string;
  price: string | null;
  tokId: string | number;
  aid: number;
  imgurl: string | null;
  meta: any;
  /** The collection's NFT contract — needed for on-chain sale reads. */
  contractAddress?: string;
  hash?: string | null;
  onchain?: boolean;
  onchainimg?: string | null;
  ownerAddress?: string | null;
  /** Per-token rarity tier from ipfs_metadata (bronze|silver|gold|platinum|diamond). */
  rarityTier?: string | null;
}

interface UseCollectionReturn {
    // State
    cDat: any | null;
    files: FileItem[];
    nfts: any[];
    item: any[];
    show: boolean;
    mContent: string;
    moTitle: any;
    volData: any | null;
    volPer: string;
    tokenDataReturn: any[];
    needsNetworkSwitch: boolean;
    failedImages: Set<string>;
    searchTerm: string;
    setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
    searchPage: number;
    setShow: React.Dispatch<React.SetStateAction<boolean>>;
    setMC: React.Dispatch<React.SetStateAction<string>>;
    activeItem: any;
    setSearchPage: React.Dispatch<React.SetStateAction<number>>;
    pagination: { total: number; page: number; totalPages: number; hasNext: boolean; hasPrev: boolean };
    // Functions
    getInf: () => Promise<any | null>;
    doSearch: (start?: number) => void;
    /**
     * Patch ONE token in the already-loaded grid, in place.
     *
     * The listing lifecycle actions (sell / cancel / remove / finalize) all end
     * with "this token's on-chain listing state just changed". Calling doSearch()
     * to reflect that refetches the whole page — a network round trip that also
     * resets scroll position, re-requests every image and can reorder the grid,
     * for a change to a single card.
     *
     * This mutates only the matching nfts entry so `files` (a useMemo over
     * `nfts`) recomputes for that token alone and the "For Sale" badge + price
     * update immediately, with no refetch.
     *
     * `auctionID: 0` (or null) clears the badge, since the grid gates on
     * `aid > 0`. Anything not supplied is left untouched.
     */
    patchTokenListing: (tokId: string | number, patch: { auctionID?: number | null; price?: string | null }) => void;
    alterPos: (dir: "prev" | "next") => void;
    uploadCollImg: (e: React.ChangeEvent<HTMLInputElement>) => void;
    mintItem: () => void;
    getItemAndShowModal: (coll: string, itemId: string | number, imgUrl?: string, knownMeta?: Record<string, any> | null, auctionId?: number | string | null, contractAddress?: string, ownerAddress?: string | null) => Promise<void>;
    handleNetworkSwitch: () => Promise<void>;
    markImageAsFailed: (id: string) => void;
    handlePeriodChange: (value: string) => void;
    navigate: ReturnType<typeof useNavigate>;
}

/**
 * Custom hook for Collection page logic.
 *
 * Encapsulates all data management and business logic for the collection detail page.
 * Handles:
 * - Collection info fetching
 * - NFT metadata retrieval and processing
 * - Search functionality with debouncing
 * - Pagination (alterPos)
 * - Volume statistics
 * - Network switching detection and handling
 * - Image upload for collection
 * - Minting initiation (placeholder)
 * - Item modal display
 *
 * @param initialCollectionId - Collection ID from route
 * @param initialData - Optional preloaded initial data from route loader
 * @returns Hook state and functions
 */
export const useCollection = (
    initialCollectionId: string,
    initialData: InitialData = {}
): UseCollectionReturn => {
    const navigate = useNavigate();
    const location = useLocation();

    // Destructure initial data from loader
    const { initialCInfo, initialNfts, initialVolData } = initialData;

    // State - initialize from props if provided
    const [cDat, setCDat] = useState(initialCInfo || null);
    const [nfts, setNfts] = useState(initialNfts || []);
    const [item, setItem] = useState<any[]>([]);
    const [show, setShow] = useState(false);
    const [mContent, setMC] = useState("No Content Yet!");
    const [moTitle, setMoT] = useState<any>();
    // activeItem drives the shared NftDetailModal (item/meta/image/noMeta/title).
    const [activeItem, setActiveItem] = useState<any>(null);
    const [volData, setVolData] = useState(initialVolData || null);
    const [volPer, setVolPer] = useState<string>("month");
    const [tokenDataReturn, setTD] = useState<any[]>([]);
    const [needsNetworkSwitch, setNeedsNetworkSwitch] = useState(false);
    const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
    const [searchTerm, setSearchTerm] = useState<string>('');

    // Refs
    const searchTimeout = useRef<NodeJS.Timeout | null>(null);
    const startPos = useRef(0);

    // AppKit hooks
    const { walletProvider } = useAppKitProvider("eip155");
    const { isConnected } = useAppKitAccount();
    const { networkData } = useAppKitNetwork();
    const collectionChainId = useMemo(() => {
        const raw = (cDat as any)?.chainId;
        if (raw === undefined || raw === null) return undefined;
        const parsed = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
        return Number.isFinite(parsed) ? parsed : undefined;
    }, [cDat]);
    const chainRpcProvider = useMemo(() => {
        const row = (cDat as any)?.chainData;
        const rpc = row?.rpc || (typeof collectionChainId === 'number' ? (chainData as any)?.[collectionChainId]?.rpc : undefined);
        if (!rpc) return null;
        try { return new ethers.providers.JsonRpcProvider(rpc); } catch { return null; }
    }, [cDat, collectionChainId]);
    const chainPublicClient = chainRpcProvider;

    // Derived: get collection ID from props
    const collectionId = initialCollectionId;

    // Core functions
    const getInf = useCallback(async () => {
        try {
            console.log('getInf: fetching collection info for collectionId =', collectionId);
            const dat = await getCollectionInfo({ collId: collectionId });
            console.log('getInf: collection info response =', dat);
            setCDat(dat);
            return dat;
        } catch (error) {
            console.error('Failed to fetch collection info:', error);
            return null;
        }
    }, [collectionId]);

    const getVolData = useCallback(async (coll: any) => {
        if (coll?.contractAddress) {
            const vd = await getVolumeInfo(coll.contractAddress);
            setVolData(vd);
        }
    }, []);

    const [searchPage, setSearchPage] = useState(1);
    const [pagination, setPagination] = useState<{ total: number; page: number; limit: number; totalPages: number; hasNext: boolean; hasPrev: boolean }>({
      total: 0,
      page: 1,
      limit: 40,
      totalPages: 1,
      hasNext: false,
      hasPrev: false,
    });

    const searchCall = useCallback(async (args: string, coll: string, start: number) => {
      try {
        console.log('searchCall: collectionId =', coll, 'start =', start, 'search =', args);
        const response = await searchNFTs(coll, {
          offset: start ?? 0,
          search: args,
          limit: 40,
          order: 'listings'
        });
        console.log('searchCall: raw response =', response);
        const out = response.items;
        console.log('searchCall: extracted data array length =', out.length);
        setNfts(out);
        setPagination({
          total: response.pagination.total,
          page: response.pagination.page,
          limit: response.pagination.limit,
          totalPages: response.pagination.totalPages,
          hasNext: response.pagination.hasNext,
          hasPrev: response.pagination.hasPrev,
        });
        return out;
      } catch (error) {
        console.error('Search failed:', error);
        return [];
      }
    }, []);

    // Patch a single token's listing state in the loaded grid (see the
    // interface doc). Keyed on `for_coll_id + tokId` like every other read here.
    //
    // Two subtleties this has to get right:
    //   1. tokId is compared as a STRING. The backend returns tokId as a string
    //      (bigint column) while the on-chain path yields a number, so a strict
    //      === would silently miss and the badge would never clear.
    //   2. The returned array is a NEW array with only the matching element
    //      replaced. `files` is a useMemo over `nfts`, so a mutated-in-place
    //      array would NOT recompute and the UI would not update at all.
    const patchTokenListing = useCallback((tokId: string | number, patch: { auctionID?: number | null; price?: string | null }) => {
        const want = String(tokId);
        setNfts((prev) => {
            let hit = false;
            const next = prev.map((n: any) => {
                if (String(n?.tokId) !== want) return n;
                hit = true;
                return { ...n, ...patch };
            });
            // No match means this token isn't on the current page of the grid —
            // nothing to update, and no reason to rerender.
            return hit ? next : prev;
        });
    }, []);

    const doSearch = useCallback((start: number = startPos.current) => {
        function runSearch(searchStart: number) {
            const pvl = document.getElementById('pvList');
            const pvh = pvl?.clientHeight;
            if (pvh) pvl.style.minHeight = pvh + "px";
            searchCall(searchTerm, collectionId, searchStart);
        }

        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        searchTimeout.current = setTimeout(() => {
            runSearch(start);
        }, 1000);
    }, [searchTerm, collectionId, searchCall]);

    const alterPos = useCallback((dir: "prev" | "next", current?: number, setCurrent?: (v: number) => void) => {
      const limit = pagination.limit || 40;
      const total = pagination.total || 0;
      const start = typeof current === 'number' ? current : startPos.current;
      if (dir === "prev") {
        const next = Math.max(0, start - limit);
        if (setCurrent) setCurrent(next);
        startPos.current = next;
        doSearch(next);
      } else if (pagination.hasNext) {
        const next = start + limit;
        if (setCurrent) setCurrent(next);
        startPos.current = next;
        doSearch(next);
      }
    }, [doSearch, pagination]);

    // NFT metadata processing — synchronous derive so SSR prepopulates the
    // grid with real CDN urls. (Previously in a useEffect, which never runs
    // on the server, so server HTML shipped an empty #pvList.)
    const files: FileItem[] = useMemo(() => {
        if (!nfts.length) return [];
        const tmp: FileItem[] = [];
        for (let i = 0; i < nfts.length; i++) {
            // A token whose metadata JSON hasn't been fetched yet (metadata
            // null / not parseable) must still render a card — the grid draws
            // an animated placeholder for it — rather than being silently
            // dropped, which made freshly-listed tokens invisible.
            let md: any = {};
            try {
                const parsed = JSON.parse(nfts[i].metadata);
                if (parsed && typeof parsed === 'object') md = parsed;
            } catch (e) {
                // null / malformed metadata — fall back to the placeholder card.
            }
            let resolvedImg: string | null = null;
            if (nfts[i].onchainimg && nfts[i].hash) {
                resolvedImg = `https://cdn.stealthisnft.xyz/c/${nfts[i].hash.substring(0, 2)}/${nfts[i].hash}.jpg`;
            } else {
                const img = md.image || null;
                const url = img ? ipfs2md5(img, true) : null;
                resolvedImg = url ? url + '.jpg' : null;
            }
            const metaWithImage = md && Object.keys(md).length ? md : {};
            if (resolvedImg && !metaWithImage.image) metaWithImage.image = resolvedImg;
            tmp.push({
                coll: nfts[i].for_coll_id,
                price: nfts[i]?.humanPrice || nfts[i]?.price || null,
                tokId: nfts[i].tokId,
                aid: nfts[i]?.auctionID,
                imgurl: resolvedImg,
                meta: metaWithImage,
                contractAddress: (cDat as any)?.contractAddress,
                hash: nfts[i].hash ?? null,
                onchain: !!nfts[i].onchain,
                onchainimg: nfts[i].onchainimg ?? null,
                ownerAddress: nfts[i]?.currentOwner ?? null,
                rarityTier: nfts[i]?.rarityTier ?? null,
            });
        }
        return tmp;
    }, [nfts, cDat]);

    const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
    const didRefreshRef = useRef(false);
    const nftsRef = useRef(nfts);
    nftsRef.current = nfts;

    useEffect(() => {
        if (!cDat || !nftsRef.current.length || !chainPublicClient) return;
        if (didRefreshRef.current) return;

        const contractAddress = (cDat as any)?.contractAddress;
        const network = cDat?.network ?? cDat?.chainId;
        if (!contractAddress || network === undefined || network === null) return;

        const candidates = nftsRef.current.filter((n: any) => n?.onchain && n?.onchainimg && n?.hash);
        if (!candidates.length) return;

        didRefreshRef.current = true;

        const run = async () => {
            const updates: Array<{ tokId: string | number; hash?: string | null; metadata?: any; imageUrl?: string | null }> = [];

            await Promise.all(
                candidates.map(async (n: any) => {
                    const tokId = n.tokId;
                    const hash = n.hash || null;
                    const existingMeta = (() => {
                        try { return JSON.parse(n.metadata || '{}'); } catch { return {}; }
                    })();

                    let newImage = existingMeta?.image || n.imgurl || null;
                    let newMeta: any = existingMeta;

                    if (hash) {
                        const cdnImg = `https://cdn.stealthisnft.xyz/c/${String(hash).substring(0, 2)}/${hash}.jpg`;
                        try {
                            await new Promise<void>((resolve) => {
                                const img = new Image();
                                img.onload = () => resolve();
                                img.onerror = () => resolve();
                                img.src = cdnImg;
                            });
                            newImage = cdnImg;
                        } catch {
                            // ignore image probe failures; keep existing image
                        }
                    }

                    const onchainResult = await fetchOnChainMetaForItem({ provider: chainPublicClient, contractAddress, tokId });
                    if (onchainResult?.metadata && typeof onchainResult.metadata === 'object') {
                        newMeta = onchainResult.metadata;
                        if (newMeta?.image) newImage = newMeta.image;
                    }

                    if (!newMeta || typeof newMeta !== 'object') return;

                    updates.push({
                        tokId,
                        hash,
                        metadata: newMeta,
                        imageUrl: newImage,
                    });
                })
            );

            if (!updates.length) return;

            await postBatchMetaUpdate(baseUrl, {
                collectionId: cDat?.id,
                contractAddress,
                network,
                items: updates,
            });

            setNfts((prev) => {
                const byId = new Map(prev.map((n: any) => [String(n.tokId), n]));
                for (const u of updates) byId.set(String(u.tokId), { ...(byId.get(String(u.tokId)) || {}), ...u });
                return Array.from(byId.values());
            });
        };

        run().catch(() => {
            didRefreshRef.current = false;
        });
    }, [cDat, chainPublicClient, baseUrl]);

    // Effects
    useEffect(() => {
        // Initial fetch - only if no initial data provided by the route loader.
        // The loader already fetched collection info; reuse it instead of refetching.
        if (!initialCInfo) {
            getInf();
        }
        // Only fetch NFTs if the loader did NOT provide them. A provided array
        // (including an empty one) is trusted — an empty collection should not
        // trigger a redundant nftMetaById call that just returns empty again.
        if (initialNfts === undefined) {
            doSearch(0);
        }
        // If initialNfts were provided, they'll be processed by the processFiles effect
        return () => {
            if (searchTimeout.current) clearTimeout(searchTimeout.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        startPos.current = 0;
        doSearch(0);
    }, [searchTerm, doSearch]);

    // Network switch check
    useEffect(() => {
        const checkSwitch = async () => {
            if (!cDat) return;

            if (isConnected && cDat?.network) {
                try {
                    const row = chainData[cDat.network];
                    if (!row?.chainId) return;
                    const expectedChainId = Number(row.chainId);
                    const currentChainId = networkData?.chainId;
                    if (typeof currentChainId === 'number' && currentChainId !== expectedChainId) {
                        setNeedsNetworkSwitch(true);
                    } else {
                        setNeedsNetworkSwitch(false);
                    }
                } catch (e) {
                    console.error('Network check error:', e);
                }
            }
        };

        checkSwitch();
    }, [cDat, isConnected, networkData?.chainId]);

    // Actions
    const handleNetworkSwitch = useCallback(async () => {
        if (!cDat?.network) return;
        const netid = chainData[cDat.network].chainId;
        try {
            const { allChains } = await import('../../lib/reowncustomchains');
            await networkData.switchNetwork(allChains()[netid]);
        } catch (error) {
            toast.error("Failed to switch network. Please try again in your wallet.");
        }
    }, [cDat, networkData]);

    const uploadCollImg = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (!selectedFile || !cDat) return;

        const fdd = {
            descr: "Collection Backdrop",
            clogo: "true",
            filen: "clogo",
            tocid: cDat.id,
            colln: "null",
            tid: null,
            wall: cDat.id,
            _token: JSON.parse(sessionStorageGet('token') || "{}"),
            nft_contract: "",
            nft_currentOwner: "0x00",
            isCached: false
        };

        const formData = new FormData();
        formData.append('json', JSON.stringify(fdd));
        formData.append('blob', selectedFile);
        uploadProxy.push(formData);
    }, [cDat]);

    const mintItem = useCallback(async () => {
        toast.info("Minting functionality would be implemented here");
    }, []);

    const getItemAndShowModal = useCallback(async (coll: string, itemId: string | number, imgUrl?: string, knownMeta?: Record<string, any> | null, auctionId?: number | string | null, contractAddress?: string, ownerAddress?: string | null) => {
        try {
            // The grid already holds parsed metadata for displayed items (from the
            // SSR loader or the client nftMetaById search) — reuse it and skip the
            // per-item nftMetaByCidItem round-trip when it's available.
            let out: any = {};
            if (knownMeta) {
                out.meta = knownMeta;
            } else {
                const itemData = await getNFTMetadata(coll, itemId);
                if (!itemData) return;
                try {
                    out.meta = JSON.parse(itemData.metadata);
                } catch (e) {
                    out.meta = {};
                }
            }
            out.coll = coll;
            out.tokId = itemId;
            // Carry the auction id onto the modal item so the detail modal can
            // show a Buy button for listed tokens. `aid` matches the field name
            // the grid uses (sourced from the backend's `auctionID`).
            out.aid = auctionId ?? null;
            // The NFT contract for this token — the on-chain sale reads in
            // SaleParticularsModal need it to query the AuctionManager.
            out.contractAddress = contractAddress ?? (cDat as any)?.contractAddress ?? null;
            out.ownerAddress = ownerAddress ?? null;

            setMoT(out?.meta?.name ? out.meta.name : cDat?.symbol + " #" + itemId);
            setActiveItem({
                item: out,
                imgUrl: imgUrl || out?.meta?.image,
                noMeta: !out?.meta?.attributes || out.meta.attributes.length === 0,
            });
            setShow(true);
        } catch (error) {
            toast.error("Failed to fetch item details");
        }
    }, [cDat, navigate, doSearch]);

    const markImageAsFailed = useCallback((id: string) => {
        setFailedImages(prev => new Set(prev).add(id));
    }, []);

    // Controlled handlers for volume stats
    const handlePeriodChange = useCallback((value: string) => {
        setVolPer(value);
    }, []);

    return {
        // State
        cDat,
        files,
        nfts,
        item,
        show,
        mContent,
        moTitle,
        volData,
        volPer,
        tokenDataReturn,
        needsNetworkSwitch,
        failedImages,
        searchTerm,
        setSearchTerm,
        setShow,
        setMC,
        activeItem,
        searchPage,
        setSearchPage,
        pagination,
        // Functions
        getInf,
        doSearch,
        patchTokenListing,
        alterPos,
        uploadCollImg,
        mintItem,
        getItemAndShowModal,
        handleNetworkSwitch,
        markImageAsFailed,
        handlePeriodChange,
        navigate
    };
};

export default useCollection;
